<template>
  <div ref="dropdownComponent" class="w-fit h-fit relative my-0 mx-0 flex align-middle">
    <div class="hover:cursor-pointer" @click="toggleMenu">
      <div
        v-if="title == undefined && titleIcon"
        :class="
          titleClass == null
            ? 'text-gray-500 min-w-8 p-10 text-12 border-0 float-none shadow-none rounded-none whitespace-nowrap overflow-hidden text-ellipsis hover:cursor-pointer'
            : titleClass
        "
        :style="titleStyle"
      >
        <i :class="titleIcon" />
      </div>
      <div v-if="titleImage">
        <img :src="titleImage" alt="User Profile Picture" :style="titleStyle" />
      </div>

      <div
        v-if="title != undefined && !titleIcon"
        :class="
          titleClass == null
            ? 'text-gray-500 min-w-8 p-10 text-12 border-0 float-none shadow-none rounded-none whitespace-nowrap overflow-hidden text-ellipsis hover:cursor-pointer'
            : titleClass
        "
        :style="titleStyle"
      >
        {{ title }}
        <i v-if="showCaret" class="fas fa-caret-down ml-2" />
      </div>
      <div v-if="custom" :class="customClass">
        <slot />
        <i v-if="showCaret" class="fas fa-caret-down -ml-8 mr-8 hover:text-primary-600" />
      </div>
    </div>

    <div
      v-if="showMenu && listOptions"
      class="min-w-200 rounded-8 fixed right-auto top-auto z-50 w-max origin-top-right bg-navigation text-white overflow-hidden cursor-pointer"
    >
      <ul class="m-0 p-0">
        <li
          v-for="(option, index) in listOptions"
          :key="index"
          :class="[
            'w-full overflow-hidden p-10 hover:bg-gray-700 list-none',
            option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          ]"
          @click="updateOption(option)"
        >
          <hr v-if="option.border" class="p-4" />
          <span v-if="option.value === 'version'" class="text-13 flex text-white items-center gap-8 overflow-hidden">
            <i v-if="option.icon" :class="option.icon" />
            <div v-text="option.text" />
          </span>
          <span v-else class="text-13 flex text-white items-center gap-8 overflow-hidden">
            <i v-if="option.icon" :class="option.icon" />
            <div v-text="option.text" />
          </span>
        </li>
      </ul>
    </div>

    <div
      v-if="showMenu && elementOptions && notifications"
      class="fixed top-auto overflow-y-scroll right-auto z-50 float-left w-200 py-2 px-0 list-none text-11 text-left bg-white border border-gray-300 rounded-4 shadow-lg overflow-x-hidden"
    >
      <DropdownMenuNotifications :element-options="elementOptions" @last-element-deleted="toggleMenu" />
    </div>

    <div
      v-if="showMenu && elementOptions && canvasactions"
      class="fixed top-auto overflow-y-scroll right-auto z-50 float-left w-200 py-2 px-0 list-none text-11 text-left bg-white border border-gray-300 rounded-4 shadow-lg overflow-x-hidden"
    >
      <DropdownMenuCanvasActions :element-options="elementOptions" @clicked-value="updateOption($event)" />
    </div>
  </div>
</template>

<script>
// USAGE IN TEMPLATE
// <DropdownMenu
//         :listOptions="partStats"
//         :closeOnOutsideClick="true"
//         :title="part.part_stat"
//         :titleStyle="{ color: statusColor }"
//         @updateOption="show($event)"
// />

import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useGlobalDropdown } from '@/composables/useGlobalDropdown.js';
import DropdownMenuCanvasActions from './DropdownMenuCanvasActions.vue';
import DropdownMenuNotifications from './DropdownMenuNotifications.vue';

export default {
  name: 'DropdownMenu',

  components: { DropdownMenuNotifications, DropdownMenuCanvasActions },

  props: {
    title: { type: String, default: undefined },
    titleIcon: { type: String, default: undefined },
    titleImage: {
      default: null,
      type: String,
    },

    custom: { type: Boolean },

    closeClickStyle: { type: String, default: undefined },
    listOptions: {
      type: Array,
      default() {
        return [];
      },
    },

    elementOptions: {
      type: Object,
      default() {
        return {};
      },
    },

    titleClass: { type: String, default: null },
    customClass: { type: String, default: null },
    titleStyle: {
      type: Object,
      default() {
        return {};
      },
    },

    showCaret: Boolean,

    notifications: { type: Boolean },
    canvasactions: { type: Boolean },

    // Optional prop to provide a unique ID for this dropdown
    dropdownId: { type: String, default: null },
  },

  emits: ['update-option'],

  setup(props) {
    const { registerDropdown, unregisterDropdown, isDropdownOpen, toggleDropdown, closeDropdown } = useGlobalDropdown();

    // Generate unique ID if not provided
    const uniqueDropdownId = props.dropdownId || `dropdown-${Math.random().toString(36).substr(2, 9)}`;

    // Register this dropdown instance
    onMounted(() => {
      registerDropdown(uniqueDropdownId);
    });

    // Unregister when component is destroyed
    onBeforeUnmount(() => {
      unregisterDropdown(uniqueDropdownId);
    });

    const showMenu = computed(() => isDropdownOpen(uniqueDropdownId));

    return {
      showMenu,
      uniqueDropdownId,
      toggleDropdown: () => toggleDropdown(uniqueDropdownId),
      closeDropdown: () => closeDropdown(uniqueDropdownId),
    };
  },

  data() {
    return {};
  },

  watch: {
    showMenu(newValue, oldValue) {
      if (newValue && !oldValue) {
        // Dropdown just opened
        this.$nextTick(() => {
          this.positionDropdown();
          setTimeout(() => {
            document.addEventListener('click', this.clickHandler, false);
          }, 0);
        });
      } else if (!newValue && oldValue) {
        // Dropdown just closed
        document.removeEventListener('click', this.clickHandler, false);
      }
    },
  },

  mounted() {
    window.addEventListener('resize', this.handleResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('click', this.clickHandler, false);
  },

  methods: {
    updateOption(option) {
      if (option.disabled) return;
      if (option.value === 'version') return;

      // Close the dropdown using global system
      this.closeDropdown();
      document.removeEventListener('click', this.clickHandler, false);

      this.$emit('update-option', option);
    },

    handleResize() {
      if (this.showMenu) {
        this.positionDropdown();
      }
    },

    toggleMenu() {
      this.toggleDropdown(); // Use the global toggle method
      // Positioning and event handling is now done in the watcher
    },

    positionDropdown() {
      const trigger = this.$refs.dropdownComponent;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const dropdowns = this.$el.querySelectorAll('[class*="min-w-200"], [class*="w-200"]');

      dropdowns.forEach(dropdown => {
        if (dropdown) {
          dropdown.style.top = `${rect.bottom}px`;
          dropdown.style.left = `${rect.left}px`;

          const dropdownRect = dropdown.getBoundingClientRect();
          if (dropdownRect.right > window.innerWidth) {
            dropdown.style.left = `${rect.right - dropdownRect.width}px`;
          }
        }
      });
    },

    clickHandler(event) {
      if (!event) return;

      const { target } = event;
      const { $el } = this;

      if (!$el?.contains(target)) {
        this.closeDropdown(); // Close using global system
        document.removeEventListener('click', this.clickHandler, false);
      }
    },
  },
};
</script>
